import styles from "../admin.module.css";
import { useEffect, useState } from "react";
import { TemplateCollection } from "PosterEditor/model/template-collection";
import { ProjectTemplate } from "PosterEditor/model/project-template";
import { useCollectionStore } from "entity-store/collections-store/use-collection-store";
import { ToolboxComponentInput as Input } from "PosterEditor/sidebar/toolboxes/toolbox-components/toolbox-component-input";
import { CTAButton } from "BEDROCK/shared-components/cta-button";
import { TemplateListModal } from "./template-list-modal";
import { ProjectPreview } from "components/common/project-preview";
import { ListItem, ListItemAction } from "admin/shared-components/list-item";
import { Reorder } from "motion/react";
import { AdminBreadcrumbs, BreadcrumbItem } from "./admin-breadcrumbs";
import { usePaths } from "BEDROCK/paths";
import { useDuplicateTemplate } from "admin/hooks/use-duplicate-template";
import { template } from "lodash";
import { on } from "events";

interface CollectionDetailAdminProps {
  collectionID?: string;
}

export const CollectionDetailAdmin = (props: CollectionDetailAdminProps) => {
  const { loadCollection, saveCollection, isLoading } = useCollectionStore();
  const [collectionID, setCollectionID] = useState<string | undefined>(
    props.collectionID
  );
  const [collection, setCollection] = useState<
    TemplateCollection | undefined
  >();

  const [updatedTemplates, setUpdatedTemplates] = useState<
    ProjectTemplate[] | undefined
  >();

  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [successMessage, setSuccessMessage] = useState<string | undefined>();
  const [unsavedChanges, setUnsavedChanges] = useState(false);

  const onUpdateTemplates = (
    templates: ProjectTemplate[],
    saveNow: boolean
  ) => {
    setUpdatedTemplates(templates);
    if (saveNow) {
      saveChanges(templates);
    }
  };

  useEffect(() => {
    const getCollectionIDFromURL = async () => {
      const path = window.location.pathname;
      const parts = path.split("/");
      const id = parts[parts.length - 1];
      if (id) {
        setCollectionID(id);
      }
    };
    getCollectionIDFromURL();
  }, []);

  useEffect(() => {
    if (updatedTemplates) {
      setUnsavedChanges(true);
    }
  }, [updatedTemplates]);

  useEffect(() => {
    if (unsavedChanges) {
      setSuccessMessage(undefined);
      setErrorMessage(undefined);
    }
  }, [unsavedChanges]);

  const saveChanges = async (templates?: ProjectTemplate[]) => {
    if (!collection) {
      return;
    }
    setErrorMessage(undefined);
    const templateIDs =
      (templates ?? updatedTemplates)?.map((t) => t.id) ??
      collection.templateIDs;

    const updatedCollection: TemplateCollection = {
      ...collection,
      templateIDs: templateIDs,
    };
    setCollection(updatedCollection);
    try {
      await saveCollection(updatedCollection);
      markSuccess("Collection saved");
    } catch {
      markError("Failed to save collection");
    }
    setUnsavedChanges(false);
  };

  const markSuccess = (message: string) => {
    setSuccessMessage(message);
    setErrorMessage(undefined);
    setTimeout(() => {
      setSuccessMessage(undefined);
    }, 2000);
  };

  const markError = (message: string) => {
    setErrorMessage(message);
    setSuccessMessage(undefined);
    setTimeout(() => {
      setErrorMessage(undefined);
    }, 2000);
  };

  useEffect(() => {
    if (collectionID) {
      loadCollection(collectionID).then((c) => {
        if (c) {
          setCollection(c);
        } else {
          setErrorMessage("Collection not found");
        }
      });
    }
  }, [collectionID]);

  if (!collection || isLoading) {
    return <div>Loading...</div>;
  }
  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <TitleBar
          collectionTitle={collection.name}
          unsavedChanges={unsavedChanges}
          onClickSaveChanges={() => void saveChanges()}
          successMessage={successMessage}
          errorMessage={errorMessage}
        />
        <div className={styles.editContainer}>
          <MetaContent
            collection={collection}
            onChangeName={(name) => {
              if (collection) {
                const updatedCollection = { ...collection, name };
                setCollection(updatedCollection);
                setUnsavedChanges(true);
              }
            }}
          />
          <TemplateList
            collection={collection}
            onEditTemplates={(templates, saveNow) => {
              onUpdateTemplates(templates ?? [], saveNow);
            }}
          />
        </div>
      </div>
    </div>
  );
};

interface TitleBarProps {
  collectionTitle: string;
  unsavedChanges: boolean;
  onClickSaveChanges: () => void;
  successMessage?: string;
  errorMessage?: string;
}

const TitleBar = (props: TitleBarProps) => {
  const { collectionsAdmin } = usePaths();
  const breadcrumbs: BreadcrumbItem[] = [
    {
      label: "Collections",
      path: collectionsAdmin,
    },
    {
      label: props.collectionTitle,
    },
  ];

  return (
    <div className={styles.title}>
      <div className={styles.titleContentLeading}>
        {/* <CTAButton variant="LINK" title="Collections" />
        <UIIcon iconName="CHEVRON-RIGHT" variant="REGULAR" size={24} />
        <h1>{props.collectionTitle}</h1> */}
        <AdminBreadcrumbs items={breadcrumbs} />
      </div>
      <div>
        {props.unsavedChanges && (
          <CTAButton
            variant="LINK"
            title="Save changes"
            onClick={props.onClickSaveChanges}
          />
        )}
        {props.successMessage && (
          <p className={styles.saveSuccess}>{props.successMessage}</p>
        )}
        {props.errorMessage && (
          <p className={styles.saveError}>{props.errorMessage}</p>
        )}
      </div>
    </div>
  );
};

interface MetaContentProps {
  collection: TemplateCollection;
  onChangeName: (name: string) => void;
}

const MetaContent = (props: MetaContentProps) => {
  return (
    <div className={styles.editMetaContent}>
      <div className={styles.editMetaContentItem}>
        <Input text={props.collection.name} onChangeText={props.onChangeName} />
      </div>
      <p>{props.collection.id}</p>
    </div>
  );
};

interface TemplateListProps {
  collection: TemplateCollection;
  onEditTemplates: (templates: ProjectTemplate[], saveNow: boolean) => void;
}

const TemplateList = (props: TemplateListProps) => {
  const paths = usePaths();
  const { duplicateTemplate } = useDuplicateTemplate();
  const { loadTemplatesForCollection } = useCollectionStore();
  const [isTemplateListModalOpen, setIsTemplateListModalOpen] =
    useState<boolean>(false);

  const [templates, setTemplates] = useState<ProjectTemplate[] | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const addOrRemoveTemplate = (template: ProjectTemplate) => {
    const index = templates?.findIndex((t) => t.id === template.id);
    if (index !== undefined && index > -1) {
      const newTemplates = [...templates!];
      newTemplates.splice(index, 1);
      setTemplates(newTemplates);
      props.onEditTemplates(newTemplates, false);
    } else {
      const newTemplates = [...(templates ?? []), template];
      setTemplates(newTemplates);
      props.onEditTemplates(newTemplates, false);
    }
  };

  const reorderTemplates = (newTemplates: ProjectTemplate[]) => {
    setTemplates(newTemplates);
    props.onEditTemplates(newTemplates, false);
  };

  const removeTemplate = (template: ProjectTemplate) => {
    //filter this template out of templates
    if (!templates) return;
    const newTemplates = templates?.filter((t) => t.id !== template.id);
    setTemplates(newTemplates);
    props.onEditTemplates(newTemplates, true);
  };

  const editTemplate = (template: ProjectTemplate) => {
    paths.navigateTo(paths.templateEditor + "/" + template.id);
  };

  const duplicate = (template: ProjectTemplate) => {
    duplicateTemplate.mutateAsync(template).then((newTemplate) => {
      const newTemplates = [...(templates ?? []), newTemplate];
      setTemplates(newTemplates);
      props.onEditTemplates(newTemplates, true);
    });
  };

  useEffect(() => {
    if (!props.collection.templateIDs.length) return;
    setIsLoading(true);
    loadTemplatesForCollection(props.collection).then((t) => {
      setTemplates(t);
      setIsLoading(false);
    });
  }, [props.collection.templateIDs.join(",")]); // 👈 trigger when IDs change

  if (isLoading && !templates) {
    return <div className={styles.loading}>Loading templates...</div>;
  }
  return (
    <div className={styles.list}>
      <div className={styles.listActions}>
        <CTAButton
          variant="LINK"
          iconName="PLUS"
          title="Add template"
          onClick={() => setIsTemplateListModalOpen(true)}
        />
      </div>
      {templates && templates.length > 0 && (
        <div className={styles.editTemplateList}>
          <Reorder.Group
            as="div"
            values={templates}
            onReorder={reorderTemplates}
            className={styles.reorderGroup}
          >
            {templates.map((t) => (
              <Reorder.Item
                key={t.id}
                value={t}
                style={{ listStyleType: "none" }}
              >
                <TemplateItem
                  template={t}
                  onClickEdit={() => {
                    editTemplate(t);
                  }}
                  onClickRemove={() => {
                    removeTemplate(t);
                  }}
                  onClickDuplicate={() => {
                    duplicate(t);
                  }}
                />
              </Reorder.Item>
            ))}
          </Reorder.Group>
        </div>
      )}

      <TemplateListModal
        isOpen={isTemplateListModalOpen}
        onClose={() => {
          setIsTemplateListModalOpen(false);
        }}
        selectedTemplates={templates ?? []}
        onSelectTemplate={(template) => {
          addOrRemoveTemplate(template);
        }}
      />
    </div>
  );

  //Load templates for collection.
};

interface TemplateItemProps {
  template: ProjectTemplate;
  onClickEdit: () => void;
  onClickRemove: () => void;
  onClickDuplicate: () => void;
}

const TemplateItem = (props: TemplateItemProps) => {
  const preview = (
    <ProjectPreview
      template={props.template}
      size={{ width: 140, height: 140 }}
    />
  );

  const actions: ListItemAction[] = [
    {
      title: "Edit",
      onClick: props.onClickEdit,
      iconName: "PENCIL",
    },
    {
      title: "Copy",
      onClick: props.onClickDuplicate,
      iconName: "BIN",
    },
    {
      title: "Remove",
      onClick: props.onClickRemove,
      iconName: "BIN",
    },
  ];

  return (
    <ListItem
      preview={preview}
      title={props.template.name}
      description={props.template.description}
      textLinkActions={actions}
    />
  );
};
